import { Fragment, type ReactNode } from 'react';
import { Alert } from '@shared/components';
import {
    BLOCK_EMPTY_MESSAGES,
    BLOCK_TITLES,
    MOTORPLAN_JOURNAL_NOTE,
    blockUnsplitNotice,
} from '../../../../utils/fundStatementLabels';
import type { StatementLine, VatStatementBlock } from '../../../../utils/statementBlocks';
import { BlockTotalRow } from './BlockTotalRow';
import { STATEMENT_GRID } from './statementGrid';
import { StatementLineRow } from './StatementLineRow';

export type RenderLine = (line: StatementLine, block: VatStatementBlock) => ReactNode;

interface Props {
    block: VatStatementBlock;
    currency: string;
    renderLine?: RenderLine;
}

const SPLIT_HEADINGS = ['VAT rate', 'Gross', 'VAT', 'Net'];
const NET_HEADINGS = ['Net'];

function BlockHeading({ block }: { block: VatStatementBlock }) {
    const headings = block.split ? SPLIT_HEADINGS : NET_HEADINGS;
    return (
        <div className={`${STATEMENT_GRID} items-end`}>
            <h3 className={`text-sm font-semibold ${block.split ? '' : 'col-span-4'}`}>{BLOCK_TITLES[block.key]}</h3>
            {headings.map((heading) => (
                <span key={heading} aria-hidden="true" className="text-right text-xs text-mp-muted">
                    {heading}
                </span>
            ))}
        </div>
    );
}

export function StatementBlockSection({ block, currency, renderLine }: Props) {
    const title = BLOCK_TITLES[block.key];
    const empty = block.lines.length === 0;
    return (
        <section aria-label={title} className="space-y-1">
            <BlockHeading block={block} />
            {empty ? (
                <p className="text-sm italic text-mp-muted">{BLOCK_EMPTY_MESSAGES[block.key]}</p>
            ) : (
                <dl className="space-y-1">
                    {block.lines.map((line) =>
                        renderLine ? (
                            <Fragment key={line.key}>{renderLine(line, block)}</Fragment>
                        ) : (
                            <StatementLineRow key={line.key} line={line} split={block.split} currency={currency} />
                        ),
                    )}
                    <BlockTotalRow block={block} currency={currency} />
                </dl>
            )}
            {block.key === 'motorplan_handled' && <p className="text-xs italic text-mp-muted">{MOTORPLAN_JOURNAL_NOTE}</p>}
            {block.unsplit.postings > 0 && (
                <Alert variant="warning" role="note">
                    {blockUnsplitNotice(block.split, block.unsplit.postings, block.unsplit.gross, currency)}
                </Alert>
            )}
        </section>
    );
}
